import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios, { AxiosResponse } from "axios";
import { useParams } from "react-router";

import { API_URLS } from "src/utils/API_URLS";
import { Pane } from "src/app/components/Pane/Pane";
import { useShowPane } from "src/hooks/useShowPane";
import { setCreatorMessagePaneOpen } from "src/redux";
import { ReactComponent as CloseIcon } from "src/images/cross-alt.svg";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { RootState } from "src/redux/reducers";
import { showErrorToast } from "src/utils/methods";
import Loader from "src/app/components/Loader/Loader";
import { TextBoxVariable } from "src/app/components/TextBox/TextBox";
import { handleError } from "src/utils/error";
import { EmailContent, MessageDetails, Steps } from "./types";
import CreatorMessagePaneCreateEmail from "./CreatorMessagePaneCreateEmail";
import CreatorMessagePaneManageEmails from "./CreatorMessagePaneManageEmails";
import CreatorMessagePaneConfirmEmail from "./CreatorMessagePaneConfirmEmail";
import { saveDraftInLocalStorage } from "./utils";

import "./CreatorMessagePane.scss";

const initialEmailContent = {
  replyToEmail: "",
  includeInCcCampaignMembers: false,
  includeInBccCampaignMembers: false,
  ccRecipients: [],
  bccRecipients: [],
  subject: "",
  message: "",
  recipients: [],
};

export default function CreatorMessagePane() {
  const dispatch = useDispatch();

  const { activeWorkspaceUuid } = useSelector(
    (state: RootState) => state.mainReducer,
  );

  const { selectedTasks } = useSelector(
    (state: RootState) => state.taskReducer,
  );

  const [messageDetails, setMessageDetails] = useState<MessageDetails | null>(
    null,
  );
  const [isGettingMessageDetails, setIsGettingMessageDetails] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isUpdatingCreatorEmails, setIsUpdatingCreatorEmails] = useState(false);
  const [variablesList, setVariablesList] = useState<TextBoxVariable[]>([]);

  const isLoading =
    isGettingMessageDetails || isSendingMessage || isUpdatingCreatorEmails;

  const [step, setStep] = useState(Steps.MANAGE_EMAILS);

  const [showPane, hidePane] = useShowPane(() => {
    dispatch(setCreatorMessagePaneOpen(false));
  });

  const [emailContent, setEmailContent] =
    useState<EmailContent>(initialEmailContent);

  const { projectId } = useParams<{
    projectId: string;
  }>();

  const recipientsWithNoEmailCRM = useMemo(() => {
    return messageDetails?.recipients.filter(
      (recipient) => recipient.emailFromCrm === null,
    );
  }, [messageDetails]);

  const selectedTasksUuids = JSON.stringify(
    selectedTasks.map((task) => task.taskId),
  );

  const getMessageDetails = useCallback(async () => {
    setIsGettingMessageDetails(true);
    const parsedSelectedTasksUuids: string[] = JSON.parse(selectedTasksUuids);
    try {
      const response: AxiosResponse<{ content: MessageDetails }> =
        await axios.post(API_URLS.postDetailsToSendMessage, {
          wsWorkspaceUuid: activeWorkspaceUuid,
          wsTasksUuids: parsedSelectedTasksUuids,
        });
      setMessageDetails(response.data.content);
    } catch (error) {
      handleError(error);
      hidePane();
      console.error(error);
    } finally {
      setIsGettingMessageDetails(false);
    }
  }, [activeWorkspaceUuid, selectedTasksUuids]);

  const handleClosePane = () => {
    saveDraftInLocalStorage(
      {
        subject: emailContent.subject,
        message: emailContent.message,
      },
      activeWorkspaceUuid,
      projectId,
    );
    hidePane();
  };

  const renderContent = () => {
    if (isLoading || !messageDetails) {
      return (
        <div className="creator-message-pane__loader">
          <Loader />
        </div>
      );
    }

    if (step === Steps.MANAGE_EMAILS) {
      return (
        <CreatorMessagePaneManageEmails
          emailContent={emailContent}
          setMessageDetails={setMessageDetails}
          setStep={setStep}
          activeWorkspaceUuid={activeWorkspaceUuid}
          recipientsWithNoEmailCRM={recipientsWithNoEmailCRM}
          setIsUpdatingCreatorEmails={setIsUpdatingCreatorEmails}
          getMessageDetails={getMessageDetails}
        />
      );
    }

    if (step === Steps.CREATE_EMAIL) {
      return (
        <CreatorMessagePaneCreateEmail
          messageDetails={messageDetails}
          setStep={setStep}
          emailContent={emailContent}
          setEmailContent={setEmailContent}
          recipientsWithNoEmailCRM={recipientsWithNoEmailCRM}
          variablesList={variablesList}
          projectId={projectId}
        />
      );
    }

    if (step === Steps.CONFIRM_EMAIL) {
      return (
        <CreatorMessagePaneConfirmEmail
          activeWorkspaceUuid={activeWorkspaceUuid}
          emailContent={emailContent}
          messageDetails={messageDetails}
          hidePane={hidePane}
          setStep={setStep}
          setIsSendingMessage={setIsSendingMessage}
          projectId={projectId}
        />
      );
    }
    return null;
  };

  useEffect(() => {
    if (!activeWorkspaceUuid || !projectId) return;
    const getVariablesForMessenger = async () => {
      try {
        const {
          data: { content },
        } = await axios.get(API_URLS.getVariablesForMessenger, {
          params: {
            wsWorkspaceUuid: activeWorkspaceUuid,
            wsProjectUuid: projectId,
          },
        });

        const newVariablesList =
          content?.variables.map(
            (variable: {
              uuid: string;
              metaFieldPrefix: string;
              name: string;
            }) => ({
              id: `${variable.metaFieldPrefix}:${variable.uuid}`,
              name: variable.name,
            }),
          ) || [];

        setVariablesList(newVariablesList);
      } catch {
        showErrorToast();
      }
    };
    getVariablesForMessenger();
  }, [activeWorkspaceUuid, projectId]);

  useEffect(() => {
    if (recipientsWithNoEmailCRM?.length === 0) {
      setStep(Steps.CREATE_EMAIL);
    }
  }, [recipientsWithNoEmailCRM]);

  useEffect(() => {
    getMessageDetails();
  }, [getMessageDetails]);

  useEffect(() => {
    if (!messageDetails) return;
    setEmailContent((prev) => ({
      ...prev,
      recipients: messageDetails.recipients,
    }));
  }, [messageDetails]);

  return (
    <Pane
      showPane={showPane}
      hidePane={handleClosePane}
      className="creator-message-pane"
    >
      <div className="creator-message-pane__topbar">
        <div className="creator-message-pane__topbar-title">
          {step === Steps.CONFIRM_EMAIL ? (
            <IDHFormattedMessage
              id="ws_confirm_sending_message"
              defaultMessage="Confirm sending message"
            />
          ) : (
            <IDHFormattedMessage
              id="ws_sending_a_message"
              defaultMessage="Sending a message"
            />
          )}
        </div>
        <CloseIcon
          onClick={handleClosePane}
          className="creator-message-pane__topbar-close-icon"
        />
      </div>
      <div className="task__container">{renderContent()}</div>
    </Pane>
  );
}
